import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'mir-utils/i18n';
import { Backdrop, Box, Stack, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Loading } from './Loading';

const useStyles = makeStyles((theme: Theme) => ({
  important: {
    color: theme.palette.secondary.main,
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
  },
}));

type Props = {
  agreeButtonText?: string;
  disagreeButtonText?: string;
  children: React.ReactElement;
  isImportant?: boolean;
  onClick?: () => void;
  text?: string;
  title?: string;
  autoFocus?: 'agree' | 'disagree';
};

/**
 * @summary Диалоговое окно для подтверждения действий пользователя.
 *
 * @param agreeButtonText - Текст, который пишется на кнопке подтверждения действия.
 * @param disagreeButtonText - Текст, который пишется на кнопке отклонения действия.
 * @param children - Элемент, нажатие на который спровоцирует появление
 * диалогового окна.
 * @param isImportant - Флаг того, что сообщение в окне является важным. Это сообщение
 * окрашивается в оранжевый цвет (secondary).
 * @param onClick - Коллбэк, который сработает при нажатии на кнопку подтверждения действия.
 * @param text - Текст, который отображается внутри диалогового окна.
 * @param title - Заголовок внутри диалогового окна.
 * @param autoFocus - Строка, указывающая на какую кнопку сделать автофокус.
 *
 * @description Если не передавать дополнительные параметры, то появится пустое окошко, у которого
 * будут только две кнопки слева направо: "Нет"(задний фон белый(default)) и "Да"(задний фон оранжевый(secondary)),
 * на кнопку "Да" сработает автофокус. Без переданной функции onClick окно при нажатии на кнопку подтверждения
 * действия просто закроется.
 * При передаче функции в onClick вида:
 *  onClick={() => action(arg)}
 * или
 *  onClick={action}
 * диалоговое окно не закроется, пока не завершится запрос.
 * При передаче функции вида:
 *  onClick={ () => {
 *    action()
 *  }}
 * диалоговое окно не будет ждать завершение запроса.
 */
export const DialogForAction = (props: Props) => {
  const {
    children,
    title,
    text,
    onClick,
    isImportant = false,
    agreeButtonText,
    disagreeButtonText,
    autoFocus = 'disagree',
  } = props;
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const classes = useStyles();
  const clone = React.cloneElement(children, {
    onClick: () => setOpen(true),
  });

  const handleClick = async () => {
    if (onClick) {
      setIsLoading(true);
      try {
        await onClick();
      } finally {
        setIsLoading(false);
        setOpen(false);
      }
    }
    setOpen(false);
  };

  return (
    <>
      {clone}
      <Dialog fullWidth open={open} onClose={() => setOpen(false)} data-testid="dialogForAction">
        <Backdrop open={isLoading} className={classes.backdrop}>
          <Loading />
        </Backdrop>
        {(title || text) && (
          <Stack spacing={2}>
            {title && <DialogTitle>{title}</DialogTitle>}
            {text && (
              <DialogContent>
                <Typography className={isImportant ? classes.important : undefined}>
                  {text}
                </Typography>
              </DialogContent>
            )}
          </Stack>
        )}
        <DialogActions disableSpacing>
          <Box mr={2}>
            <Button
              onClick={() => setOpen(false)}
              size="small"
              variant="contained"
              autoFocus={autoFocus === 'disagree'}
              data-test="DialogForActionDisagree"
            >
              {disagreeButtonText ?? t('No')}
            </Button>
          </Box>
          <Button
            onClick={handleClick}
            size="small"
            variant="contained"
            autoFocus={autoFocus === 'agree'}
            color="secondary"
            data-test="DialogForActionAgree"
          >
            {agreeButtonText ?? t('Yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
